import {
	Component,
	computed,
	ElementRef,
	inject,
	input,
	viewChild,
	AfterViewInit,
	signal,
	effect,
	OnDestroy,
	ChangeDetectionStrategy,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Editor } from '@tiptap/core';
import { StarterKit } from '@tiptap/starter-kit';
import { Markdown } from 'tiptap-markdown';
import { JeptoBaseCardComponent } from '../jepto-base-card.component';
import { JeptoUiProgressBarIndeterminateComponent } from '../../../../ui-lib/buttons-indicators/jepto-ui-progress-bar-indeterminate/jepto-ui-progress-bar-indeterminate.component';
import { DataService } from '../../../services/data.service';

/**
 * Markdown Card Component
 *
 * Renders markdown content using TipTap editor in read-only mode within a card container.
 * Supports standalone mode with direct [markdown] input or connected mode via [cardId].
 * Uses the same rendering engine as the markdown editor for consistency.
 */
@Component({
	selector: 'jepto-markdown-card',
	templateUrl: './jepto-markdown-card.component.html',
	styleUrls: ['./jepto-markdown-card.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		MatIconModule,
		MatMenuModule,
		JeptoUiProgressBarIndeterminateComponent,
	],
})
export class JeptoMarkdownCardComponent
	extends JeptoBaseCardComponent
	implements AfterViewInit, OnDestroy
{
	// Card-specific inputs
	markdown = input<string>('');
	loading = input<boolean>(false);
	/**
	 * When true (e.g. text card editor create preview), show instructional empty
	 * state instead of the generic no-content message.
	 */
	allowEmptyContent = input<boolean>(false);

	// Template reference
	private editorContainer =
		viewChild.required<ElementRef<HTMLDivElement>>('editorContainer');

	// Services
	private dataService = inject(DataService, { optional: true });

	// Internal state
	private editor: Editor | null = null;
	private editorInitialized = signal(false);

	// Computed signals
	protected effectiveMarkdown = computed(() => {
		const fromInput = this.markdown() ?? '';
		const id = this.cardId();
		if (id && this.dataService) {
			const fromService = this.dataService.contentDataSignal(id)();
			if (fromService !== undefined) {
				return fromService;
			}
		}
		return fromInput;
	});

	/** True while waiting on explicit loading or (connected mode) first content payload from DataService. */
	protected isLoading = computed(() => {
		if (this.loading()) return true;
		const id = this.cardId();
		if (id && this.dataService) {
			return this.dataService.contentDataSignal(id)() === undefined;
		}
		return false;
	});

	/** Editor create flow: instructional empty state when content is not set yet. */
	protected readonly showCreateTextEmptyState = computed(
		() => this.allowEmptyContent() && !this.effectiveMarkdown()?.trim()
	);

	constructor() {
		super();
		effect(() => {
			const md = this.effectiveMarkdown();
			if (this.editorInitialized() && this.editor) {
				this.editor.commands.setContent(md);
			}
		});
	}

	ngAfterViewInit(): void {
		this.initializeEditor();
	}

	ngOnDestroy(): void {
		if (this.editor) {
			this.editor.destroy();
			this.editor = null;
		}
	}

	private initializeEditor(): void {
		const container = this.editorContainer().nativeElement;
		if (!container || this.editor) return;

		this.editor = new Editor({
			element: container,
			extensions: [
				StarterKit.configure({
					// Ensure list extensions work properly
					bulletList: {
						HTMLAttributes: {
							class: 'bullet-list',
						},
					},
					orderedList: {
						HTMLAttributes: {
							class: 'ordered-list',
						},
					},
					listItem: {
						HTMLAttributes: {
							class: 'list-item',
						},
					},
				}),
				Markdown.configure({
					html: false,
					bulletListMarker: '-',
					tightLists: true,
					breaks: true,
				}),
			],
			content: this.effectiveMarkdown(),
			editable: false, // Read-only mode
		});

		this.editorInitialized.set(true);
	}
}
